import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { contactInfo } from "../mock";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
const API = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // <<< Add capitalizeName here
  const capitalizeName = (value) =>
    value
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    else if (!/^[a-zA-Z ]+$/.test(formData.name))
      newErrors.name = "Name can contain only letters and spaces";

    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    else if (!/^\+?\d{10,15}$/.test(formData.phone.replace(/\s/g, "")))
      newErrors.phone = "Enter a valid phone number";

    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Enter a valid email";

    if (!formData.message.trim()) newErrors.message = "Message is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "name") {
      const lettersOnly = value.replace(/[^a-zA-Z ]/g, "");
      setFormData((prev) => ({ ...prev, name: capitalizeName(lettersOnly) }));
    } else if (name === "phone") {
      let digits = value.replace(/\D/g, ""); // remove non-digit characters

      // Remove leading 91 if user types it manually
      if (digits.startsWith("91")) {
        digits = digits.slice(2);
      }

      // Limit to 10 digits
      digits = digits.slice(0, 10);

      // Always format as "+91 1234567890"
      const formattedPhone = digits ? "+91 " + digits : "";

      setFormData((prev) => ({ ...prev, phone: formattedPhone }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error if any
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await addDoc(collection(db, "contactMessages"), {
        ...formData,
        source: "Website Contact Form",
        status: "new",
        createdAt: serverTimestamp(),
      });
      await fetch(`${API}/send-contact-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      setSubmitStatus("success");
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
      setErrors({});
    } catch (error) {
      console.error("Firebase Error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen py-20"
      style={{ backgroundColor: "var(--bg-1)" }}
    >
      <div className="container-custom">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-brand-dark mb-6">
            Get In Touch
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Have questions about our projects? We're here to help. Reach out to
            us and we'll respond as soon as possible.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-brand-dark mb-6">
                  Send Us A Message
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Name */}
                    <div className="space-y-2">
                      <Label htmlFor="name">
                        Full Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        onKeyPress={(e) => {
                          const regex = /^[a-zA-Z ]$/;
                          if (!regex.test(e.key)) e.preventDefault();
                        }}
                        placeholder="Your name"
                      />
                      {errors.name && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.name}
                        </p>
                      )}
                    </div>

                    {/* Phone */}
                    <div className="space-y-2">
                      <Label htmlFor="phone">
                        Phone Number <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 XXXXX XXXXX"
                      />

                      {errors.phone && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.phone}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email">
                      Email Address <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  {/* Subject */}
                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject</Label>
                    <Input
                      id="subject"
                      name="subject"
                      type="text"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="How can we help?"
                    />
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <Label htmlFor="message">
                      Message <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us more about your requirements..."
                      rows={6}
                    />
                    {errors.message && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.message}
                      </p>
                    )}
                  </div>

                  {submitStatus === "success" && (
                    <div className="p-4 bg-green-50 text-green-800 rounded-lg text-center">
                      Thank you! Your message has been sent successfully.
                    </div>
                  )}
                  {submitStatus === "error" && (
                    <div className="p-4 bg-red-50 text-red-800 rounded-lg text-center">
                      Something went wrong. Please try again.
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-primary py-6 text-lg flex items-center justify-center"
                  >
                    <Send size={20} className="mr-2" />
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar remains same */}
          {/* Contact Information Sidebar */}
          <div className="space-y-6">
            {/* Office Info */}
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-brand-dark mb-4">
                  Office Information
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Phone
                      size={20}
                      className="text-brand-gold flex-shrink-0 mt-1"
                    />
                    <div>
                      <p className="text-sm font-semibold text-brand-dark mb-1">
                        Phone
                      </p>
                      <a
                        href={`tel:${contactInfo.phone}`}
                        className="text-sm text-brand-gold hover:underline"
                      >
                        {contactInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Mail
                      size={20}
                      className="text-brand-gold flex-shrink-0 mt-1"
                    />
                    <div>
                      <p className="text-sm font-semibold text-brand-dark mb-1">
                        Email
                      </p>
                      <a
                        href={`mailto:${contactInfo.email}`}
                        className="text-sm text-brand-gold hover:underline break-all"
                      >
                        {contactInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Clock
                      size={20}
                      className="text-brand-gold flex-shrink-0 mt-1"
                    />
                    <div>
                      <p className="text-sm font-semibold text-brand-dark mb-1">
                        Business Hours
                      </p>
                      <p className="text-sm text-[var(--brand-gold)] hover:underline">
                        {contactInfo.businessHours}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Links */}
            <Card className="bg-brand-dark text-white">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <a
                    href={`tel:${contactInfo.phone}`}
                    className="block w-full bg-brand-gold hover:bg-brand-gold/90 text-white py-3 px-4 rounded-lg text-center font-semibold transition-colors"
                  >
                    Call Now
                  </a>
                  <a
                    href={`https://wa.me/${contactInfo.whatsapp.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-lg text-center font-semibold transition-colors"
                  >
                    WhatsApp Us
                  </a>
                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="block w-full bg-white/10 hover:bg-white/20 text-white py-3 px-4 rounded-lg text-center font-semibold transition-colors"
                  >
                    Send Email
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Our Locations */}
       {/* Our Locations */}
<div className="mt-16">
  <h2 className="text-3xl font-bold text-center text-brand-dark mb-10">
    Our Locations
  </h2>

  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

    {/* Head Office */}
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <div className="p-6">
          <h3 className="text-2xl font-bold text-brand-dark mb-3">
            Head Office
          </h3>

          <div className="flex items-start gap-2 mb-5">
            <MapPin size={18} className="text-brand-gold mt-1 flex-shrink-0" />
            <p className="text-gray-600 leading-relaxed whitespace-pre-line">
              {contactInfo.addresses.headOffice}
            </p>
          </div>
        </div>

        <iframe
          src={contactInfo.maps.headOffice}
          width="100%"
          height="300"
          style={{ border: 0 }}
          loading="lazy"
          title="Head Office"
        />
      </CardContent>
    </Card>

    {/* Branch Office */}
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <div className="p-6">
          <h3 className="text-2xl font-bold text-brand-dark mb-3">
            Branch Office
          </h3>

          <div className="flex items-start gap-2 mb-5">
            <MapPin size={18} className="text-brand-gold mt-1 flex-shrink-0" />
            <p className="text-gray-600 leading-relaxed whitespace-pre-line">
              {contactInfo.addresses.branchOffice}
            </p>
          </div>
        </div>

        <iframe
          src={contactInfo.maps.branchOffice}
          width="100%"
          height="300"
          style={{ border: 0 }}
          loading="lazy"
          title="Branch Office"
        />
      </CardContent>
    </Card>

  </div>
</div>
      </div>
    </div>
  );
};

export default Contact;
